import React, { useEffect, useState } from "react";
import { FaSearch, FaCrosshairs, FaMapMarkerAlt, FaCheck, FaBuilding, FaHome, FaBriefcase } from "react-icons/fa";
import { Building2, MapPin, Sparkles, Navigation, Plus } from "lucide-react";
import useCities from "../../services/Navbar/common/LocationPopup";
import { useLocationContext } from "../../context/LocationContext";
import { usePopupLock } from "../../context/PopupLockContext";

export default function LocationPopup({ isOpen, onClose }) {
  const {
    cities,
    suggestions,
    setSuggestions,
    getAllCities,
    searchCities,
  } = useCities();

  const {
    setCityManually,
    setAddressManually,
    clearManualCity,
    fetchLocation,
    savedAddresses,
    loadSavedAddresses,
    address: currentAddress,
    selectedCity,
  } = useLocationContext();

  const { openLock, closeLock } = usePopupLock();

  const [activeTab, setActiveTab] = useState("all"); // 'all', 'saved', 'cities'
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getAllCities();
      loadSavedAddresses();
      openLock("location");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Select City
  const handleSelectCity = (city) => {
    setCityManually(city);
    const name = city?.cityName || city?.name;
    setSearch(name);
    setShowDropdown(false);
    setSuggestions([]);
    closeLock("location");
    onClose();
  };

  // Select Saved User Address
  const handleSelectAddress = (addr) => {
    setAddressManually(addr);
    closeLock("location");
    onClose();
  };

  // Auto Detect Current GPS Location
  const handleDetectLocation = () => {
    clearManualCity();
    fetchLocation(true);
    closeLock("location");
    onClose();
  };

  const handleClose = () => {
    closeLock("location");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl rounded-3xl p-6 shadow-2xl relative max-h-[90vh] flex flex-col overflow-hidden border border-slate-100 animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold">
              <MapPin size={20} />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black text-slate-900 leading-tight">
                Select Your Location
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Choose delivery address or city to see labs & tests nearby
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 text-sm font-bold transition-all"
          >
            ✕
          </button>
        </div>

        {/* Search & Detect Bar */}
        <div className="pt-4 pb-3">
          <div className="flex flex-col sm:flex-row gap-2.5">
            <div className="relative flex-1">
              <div className="flex items-center border border-slate-200 focus-within:border-red-500 px-3.5 py-2.5 rounded-xl bg-slate-50 focus-within:bg-white transition-all">
                <FaSearch className="mr-2.5 text-slate-400 text-xs shrink-0" />
                <input
                  value={search}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSearch(val);
                    if (val.trim()) {
                      setShowDropdown(true);
                      searchCities(val);
                    } else {
                      setShowDropdown(false);
                      setSuggestions([]);
                    }
                  }}
                  className="w-full outline-none text-xs sm:text-sm bg-transparent placeholder:text-slate-400 text-slate-800"
                  placeholder="Search city, area or landmark..."
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch("");
                      setShowDropdown(false);
                    }}
                    className="text-slate-400 hover:text-slate-600 text-xs ml-1"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Suggestions Dropdown */}
              {showDropdown && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1.5 bg-white shadow-xl rounded-2xl border border-slate-100 max-h-56 overflow-y-auto z-20 divide-y divide-slate-50">
                  {suggestions.map((city, i) => {
                    const name = city?.cityName || city?.name;
                    return (
                      <div
                        key={i}
                        onClick={() => handleSelectCity(city)}
                        className="px-4 py-2.5 hover:bg-red-50 text-xs sm:text-sm font-medium text-slate-700 hover:text-red-600 cursor-pointer flex items-center justify-between transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <MapPin size={14} className="text-slate-400" />
                          {name}
                        </span>
                        <span className="text-[10px] text-slate-400">Select</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={handleDetectLocation}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 text-xs font-bold transition-all shadow-xs shrink-0 cursor-pointer"
            >
              <Navigation size={14} className="animate-pulse" />
              <span>Use Current Location</span>
            </button>
          </div>
        </div>

        {/* Current Active Location Banner */}
        {currentAddress && (
          <div className="mb-4 bg-slate-50 border border-slate-200/80 rounded-2xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping shrink-0" />
              <div className="min-w-0">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Selected Location</span>
                <p className="text-xs font-bold text-slate-800 truncate">{currentAddress}</p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full shrink-0">Active</span>
          </div>
        )}

        {/* Scrollable Content (Saved Addresses + Popular Cities) */}
        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          
          {/* 1. Saved Addresses Section (If Available) */}
          {savedAddresses && savedAddresses.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <FaHome className="text-slate-400" />
                  <span>Saved Addresses</span>
                </h3>
                <span className="text-[11px] font-semibold text-slate-500">{savedAddresses.length} saved</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {savedAddresses.map((addr, i) => {
                  const title = addr.addressTitle || (addr.addressTypeID === 1 ? "Home" : "Work");
                  const fullAddress = [addr.faltHousNumber, addr.street, addr.locality, addr.pincode].filter(Boolean).join(", ");
                  const isCurrent = currentAddress && currentAddress.includes(addr.locality || addr.street || addr.addressTitle);

                  return (
                    <div
                      key={addr.id || i}
                      onClick={() => handleSelectAddress(addr)}
                      className={`p-3 rounded-2xl border text-left cursor-pointer transition-all flex items-start gap-2.5 relative ${
                        isCurrent
                          ? "border-red-500 bg-red-50/40 shadow-xs"
                          : "border-slate-200 hover:border-red-300 hover:bg-slate-50"
                      }`}
                    >
                      <div className="w-7 h-7 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0 mt-0.5 font-bold">
                        {title.toLowerCase().includes("work") ? <FaBriefcase size={12} /> : <FaHome size={12} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-slate-900 truncate">{title}</p>
                          {isCurrent && <FaCheck className="text-red-600 text-xs" />}
                        </div>
                        <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{fullAddress}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 2. Popular Cities Grid (100% Database Only) */}
          {cities && cities.length > 0 && (
            <div>
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                <Building2 size={13} className="text-slate-400" />
                <span>Popular Cities ({cities.length})</span>
              </h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {cities.map((city, i) => {
                  const name = city?.cityName || city?.name;
                  const isSelected = selectedCity?.name?.toLowerCase() === name?.toLowerCase() || currentAddress?.toLowerCase().includes(name?.toLowerCase());

                  return (
                    <button
                      key={city.name || city.cityName || i}
                      onClick={() => handleSelectCity(city)}
                      className={`p-3 rounded-2xl border text-center transition-all flex flex-col items-center justify-center gap-1 cursor-pointer ${
                        isSelected
                          ? "border-red-500 bg-red-50 text-red-600 font-bold shadow-2xs"
                          : "border-slate-200 hover:border-red-300 hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <Building2 className={`w-5 h-5 ${isSelected ? "text-red-600" : "text-slate-400"}`} />
                      <span className="text-xs font-bold truncate max-w-full">{name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer info */}
        <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <span>* Tests & packages will be filtered within 20km</span>
          <button
            onClick={handleClose}
            className="text-xs font-bold text-slate-600 hover:text-slate-900"
          >
            Cancel
          </button>
        </div>

      </div>
    </div>
  );
}